import { css } from '@emotion/react'
import { colors, font } from 'plume-ui'
import { useContext, useEffect } from 'react'
import { IcomoonIcon } from './IcomoonIcon'
import { ModalContext } from '@/contexts/ModalContext'

export type ModalProps = {
  children?: React.ReactNode
  isHidden: boolean
  zIndex?: number
  onClose: () => void
  className?: string
}

export const Modal: React.FC<ModalProps> = (props) => {
  const onChangeModalVisibility = useContext(ModalContext)

  useEffect(() => {
    onChangeModalVisibility(!props.isHidden)
  }, [props.isHidden, onChangeModalVisibility])

  return (
    <ModalOverlay
      zIndex={props.zIndex}
      isHidden={props.isHidden}
      onClose={props.onClose}
    >
      <ModalCard className={props.className}>{props.children}</ModalCard>
    </ModalOverlay>
  )
}

const ModalOverlay: React.FC<{
  children: React.ReactNode
  isHidden: boolean
  zIndex?: number
  onClose: () => void
}> = (props) => {
  if (props.isHidden) {
    return null
  }
  return (
    <div
      css={css`
        bottom: 0;
        left: 0;
        right: 0;
        top: 0;
        position: fixed;
        z-index: ${props.zIndex ?? 100};
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.3);
      `}
      onClick={props.onClose}
    >
      {props.children}
    </div>
  )
}

type ModalCardProps = {
  children: React.ReactNode
  className?: string
}

const ModalCard: React.FC<ModalCardProps> = (props) => {
  const handleClickCard = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  return (
    <div
      css={css`
        box-sizing: border-box;
        position: relative;
        padding-left: 5px;
        padding-right: 5px;
        position: fixed;
        height: auto;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: fit-content;
        max-width: fit-content;
      `}
      className={props.className}
      onClick={handleClickCard}
    >
      <div
        className="modal-content"
        css={css`
          border-radius: 5px;
          padding: 24px 32px;
          background-color: ${colors.white00};
          min-height: 150px;
        `}
      >
        <div>{props.children}</div>
      </div>
    </div>
  )
}

type ModalTitleProps = {
  children: string
  'data-test-id'?: string
}

export const ModalTitle: React.FC<ModalTitleProps> = (props) => {
  return (
    <div
      data-test-id={props['data-test-id']}
      css={css`
        line-height: 24px;
        margin: 0;
        font-size: 16px;
        text-align: left;
        color: ${colors.textStrong};
        font-weight: ${font.weight.bold};
      `}
    >
      {props.children}
    </div>
  )
}

const FOCUS_STYLE = css`
  &:focus-visible {
    outline: 2px solid ${colors.black10};
    outline-offset: 0.15rem;
    transition: initial;
    border: 1px solid ${colors.gray80};
  }
`

type ModalCloseButtonProps = {
  'data-test-id'?: string
  onClick: () => void
}

export const ModalCloseButton: React.FC<ModalCloseButtonProps> = (props) => {
  return (
    <button
      data-test-id={props['data-test-id']}
      onClick={props.onClick}
      className="close"
      css={css`
        background-color: transparent;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        padding: 0;
        appearance: none;
        width: 32px;
        height: 32px;

        /* ボタン位置の微調整 */
        position: relative;
        right: -10px;

        &:hover {
          background-color: #d9d9d9;
        }

        i.modal-close {
          font-size: 85%;
          line-height: 32px;
        }

        ${FOCUS_STYLE}
      `}
    >
      <IcomoonIcon name={'close1'} className="modal-close" />
    </button>
  )
}
